import React, { useState, useEffect, useCallback } from 'react';
import { AppContainer } from './styles/StyledComponents';
import GlobalStyles from './styles/GlobalStyles';
import MainMenu from './components/MainMenu';
import GameScreen from './components/GameScreen';
import ResultScreen from './components/ResultScreen';
import Settings from './components/Settings';
import ShopScreen from './components/ShopScreen';
import { useGame } from './hooks/useGame';

// 상점 아이템 타입
interface ShopItem {
  id: string;
  name: string;
  description: string;
  price: number;
  purchased: boolean;
}

// 화면 상태 타입
type ScreenState = 'menu' | 'game' | 'settings' | 'shop';

const App: React.FC = () => {
  // 게임 상태 및 로직
  const game = useGame();
  
  // 현재 화면 상태
  const [screen, setScreen] = useState<ScreenState>('menu');
  
  // 설정 상태
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);
  const [vibrationEnabled, setVibrationEnabled] = useState<boolean>(true);
  
  // 상점 아이템
  const [shopItems, setShopItems] = useState<ShopItem[]>([
    {
      id: 'item1',
      name: '닌자 스킨 - 레드',
      description: '빨간색 닌자 스킨으로 변경합니다.',
      price: 100,
      purchased: false
    },
    {
      id: 'item2',
      name: '닌자 스킨 - 블루',
      description: '파란색 닌자 스킨으로 변경합니다.',
      price: 100,
      purchased: false
    },
    {
      id: 'item3',
      name: '특수 능력 강화',
      description: '특수 능력 지속 시간이 2초 증가합니다.',
      price: 200,
      purchased: false
    },
    {
      id: 'item4',
      name: '코인 부스터',
      description: '획득하는 코인이 2배로 증가합니다.',
      price: 300,
      purchased: false
    }
  ]);
  
  // 로컬 스토리지에서 설정 및 상점 아이템 불러오기
  useEffect(() => {
    const savedSound = localStorage.getItem('ninjaRunnerSound');
    const savedVibration = localStorage.getItem('ninjaRunnerVibration');
    const savedShopItems = localStorage.getItem('ninjaRunnerShopItems');
    
    if (savedSound !== null) {
      setSoundEnabled(savedSound === 'true');
    }
    
    if (savedVibration !== null) {
      setVibrationEnabled(savedVibration === 'true');
    }
    
    if (savedShopItems !== null) {
      setShopItems(JSON.parse(savedShopItems));
    }
  }, []);
  
  // 설정 저장
  const saveSettings = useCallback(() => {
    localStorage.setItem('ninjaRunnerSound', soundEnabled.toString());
    localStorage.setItem('ninjaRunnerVibration', vibrationEnabled.toString());
    localStorage.setItem('ninjaRunnerShopItems', JSON.stringify(shopItems));
  }, [soundEnabled, vibrationEnabled, shopItems]);
  
  // 소리 토글
  const toggleSound = () => {
    setSoundEnabled(prev => !prev);
  };
  
  // 진동 토글
  const toggleVibration = () => {
    setVibrationEnabled(prev => !prev);
  };
  
  // 설정 화면에서 돌아올 때 설정 저장
  useEffect(() => {
    if (screen !== 'settings') {
      saveSettings();
    }
  }, [screen, saveSettings]);
  
  // 게임 시작
  const handleStartGame = () => {
    game.startGame();
    setScreen('game');
  };
  
  // 설정 화면으로 이동
  const handleOpenSettings = () => {
    setScreen('settings');
  };
  
  // 상점 화면으로 이동
  const handleOpenShop = () => {
    setScreen('shop');
  };
  
  // 메인 메뉴로 이동
  const handleMainMenu = () => {
    setScreen('menu');
  };
  
  // 아이템 구매
  const handlePurchaseItem = (itemId: string) => {
    const item = shopItems.find(item => item.id === itemId);
    
    if (item && !item.purchased && game.totalCoins >= item.price) {
      // 코인 차감
      const newTotalCoins = game.totalCoins - item.price;
      localStorage.setItem('ninjaRunnerTotalCoins', newTotalCoins.toString());
      
      // 아이템 구매 상태 업데이트
      setShopItems(prev => 
        prev.map(item => 
          item.id === itemId ? { ...item, purchased: true } : item
        )
      );
      
      // 로컬 스토리지에 저장
      saveSettings();
    }
  };
  
  // 점프 처리 (진동 추가)
  const handleJump = () => {
    game.jump();
    
    // 진동 활성화 상태이면 진동 실행
    if (vibrationEnabled && 'vibrate' in navigator) {
      navigator.vibrate(20);
    }
  };
  
  // 슬라이드 처리 (진동 추가)
  const handleSlide = () => {
    game.slide();
    
    // 진동 활성화 상태이면 진동 실행
    if (vibrationEnabled && 'vibrate' in navigator) {
      navigator.vibrate(20);
    }
  };
  
  // 공격 처리 (진동 추가)
  const handleAttack = () => {
    game.attack();
    
    // 진동 활성화 상태이면 진동 실행
    if (vibrationEnabled && 'vibrate' in navigator) {
      navigator.vibrate(20);
    }
  };
  
  // 특수 능력 사용 (진동 추가)
  const handleUseSpecial = () => {
    game.useSpecialAbility();
    
    // 진동 활성화 상태이면 진동 실행
    if (vibrationEnabled && 'vibrate' in navigator) {
      navigator.vibrate([50, 50, 50]);
    }
  };
  
  // 일시정지 토글
  const handlePauseToggle = () => {
    game.pauseGame();
  };
  
  // 현재 화면 렌더링
  const renderScreen = () => {
    // 게임 종료 화면
    if (game.status === 'ended') {
      return (
        <ResultScreen
          score={game.score}
          highScore={game.highScore}
          coins={game.coins}
          onRestart={handleStartGame}
          onMainMenu={handleMainMenu}
        />
      );
    }
    
    // 설정 화면
    if (screen === 'settings') {
      return (
        <Settings
          soundEnabled={soundEnabled}
          vibrationEnabled={vibrationEnabled}
          onToggleSound={toggleSound}
          onToggleVibration={toggleVibration}
          onBack={handleMainMenu}
        />
      );
    }
    
    // 상점 화면
    if (screen === 'shop') {
      return (
        <ShopScreen
          items={shopItems}
          totalCoins={game.totalCoins}
          onPurchase={handlePurchaseItem}
          onBack={handleMainMenu}
        />
      );
    }
    
    // 게임 화면
    if (screen === 'game') {
      return (
        <GameScreen
          score={game.score}
          highScore={game.highScore}
          coins={game.coins}
          specialMeter={game.specialMeter}
          specialActive={game.specialActive}
          isJumping={game.isJumping}
          isSliding={game.isSliding}
          obstacles={game.obstacles}
          enemies={game.enemies}
          items={game.items}
          projectiles={game.projectiles}
          onPause={handlePauseToggle}
          onJump={handleJump}
          onSlide={handleSlide}
          onAttack={handleAttack}
          onUseSpecial={handleUseSpecial}
        />
      );
    }
    
    // 메인 메뉴 (기본)
    return (
      <MainMenu
        highScore={game.highScore}
        totalCoins={game.totalCoins}
        onStartGame={handleStartGame}
        onOpenSettings={handleOpenSettings}
        onOpenShop={handleOpenShop}
      />
    );
  };
  
  return (
    <>
      <GlobalStyles />
      <AppContainer>
        {renderScreen()}
      </AppContainer>
    </>
  );
};

export default App;
